import { MainLayout } from "@/components/layout/MainLayout";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { ProvidersWrapper } from "@/components/providers/ProvidersWrapper";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { useAuth } from "@/contexts/auth-context";
import Analytics from "@/pages/Analytics";
import Brand from "@/pages/Brand";
import ContentCalendar from "@/pages/ContentCalendar";
import Dashboard from "@/pages/Dashboard";
import EmbedSignup from "@/pages/EmbedSignup";
import ForgotPassword from "@/pages/ForgotPassword";
import Integrations from "@/pages/Integrations";
import NotFound from "@/pages/NotFound";
import Onboarding from "@/pages/Onboarding";
import SEO from "@/pages/SEO";
import Settings from "@/pages/Settings";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import DetailView from "@/pages/articles/Detail";
import Content from "@/pages/articles/List";
import NewArticle from "@/pages/articles/New";
import SEOEnhancements from "@/pages/seo/Enhancements";
import SEOOverview from "@/pages/seo/Overview";
import SEOPageSpeed from "@/pages/seo/PageSpeed";
import Pricing from "@/pages/Pricing";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";

import { AuthProvider } from "./lib/auth-provider";

const queryClient = new QueryClient();

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/signin" />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" />;
}

function AppRoutes() {
  return (
    <div>
      <Routes>
        {/* Auth Routes */}
        <Route
          path="/signin"
          element={
            <PublicRoute>
              <SignIn />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/embed-signup"
          element={
            <PublicRoute>
              <EmbedSignup />
            </PublicRoute>
          }
        />
        <Route
          path="/404"
          element={
            <PublicRoute>
              <NotFound />
            </PublicRoute>
          }
        />

        {/* Protected Routes */}

        {/* Onboarding Route with its own layout */}
        <Route element={<OnboardingLayout />}>
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />
        </Route>

        {/* Main Routes with sidebar */}
        <Route element={<MainLayout />}>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/start-trial"
            element={
              <PrivateRoute>
                <Pricing mode="paywall" />;
              </PrivateRoute>
            }
          />
          <Route
            path="/plans"
            element={
              <PrivateRoute>
                <Pricing mode="billing" />
              </PrivateRoute>
            }
          />
          <Route
            path="/articles"
            element={
              <PrivateRoute>
                <Content />
              </PrivateRoute>
            }
          />
          <Route
            path="/articles/new"
            element={
              <PrivateRoute>
                <NewArticle />
              </PrivateRoute>
            }
          />
          <Route
            path="/content/calendar"
            element={
              <PrivateRoute>
                <ContentCalendar />
              </PrivateRoute>
            }
          />
          <Route
            path="/seo"
            element={
              <PrivateRoute>
                <SEO />
              </PrivateRoute>
            }
          />
          <Route
            path="/seo/overview"
            element={
              <PrivateRoute>
                <SEOOverview />
              </PrivateRoute>
            }
          />
          <Route
            path="/seo/pagespeed"
            element={
              <PrivateRoute>
                <SEOPageSpeed />
              </PrivateRoute>
            }
          />
          <Route
            path="/seo/enhancements"
            element={
              <PrivateRoute>
                <SEOEnhancements />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics />
              </PrivateRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <PrivateRoute>
                <Integrations />
              </PrivateRoute>
            }
          />
          <Route
            path="/brand"
            element={
              <PrivateRoute>
                <Brand />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/articles/:articleId"
            element={
              <PrivateRoute>
                <DetailView />
              </PrivateRoute>
            }
          />
        </Route>
      </Routes>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="creator-ui-theme">
        <Router>
          <AuthProvider>
            <ProvidersWrapper>
              <AppRoutes />
              <Toaster />
              <Sonner />
            </ProvidersWrapper>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
